import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { FREE_DELIVERY_THRESHOLD, FLAT_DELIVERY, DEMO_COUPON } from "../data/catalog";
import { fetchCatalogue } from "../lib/store";
import { toast } from "sonner";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("ayushi-cart")) || [];
    } catch {
      return [];
    }
  });
  const [catalogue, setCatalogue] = useState([]);
  const [catalogueReady, setCatalogueReady] = useState(false);
  const [coupon, setCoupon] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { products, sets } = await fetchCatalogue();
        if (alive) setCatalogue([...products, ...sets]);
      } catch (e) {
        console.error("Cart catalogue load failed:", e);
      } finally {
        if (alive) setCatalogueReady(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  const byId = useMemo(() => {
    const m = new Map();
    for (const p of catalogue) m.set(p.id, p);
    return m;
  }, [catalogue]);

  const resolve = (id) => byId.get(id) || null;

  useEffect(() => {
    if (!catalogueReady || catalogue.length === 0) return;
    setItems((prev) => {
      const kept = prev.filter((i) => byId.has(i.id));
      return kept.length === prev.length ? prev : kept;
    });
  }, [catalogueReady, catalogue.length, byId]);

  useEffect(() => {
    localStorage.setItem("ayushi-cart", JSON.stringify(items));
  }, [items]);

  const add = (id, qty = 1, silent = false) => {
    const item = resolve(id);
    setItems((prev) => {
      const found = prev.find((i) => i.id === id);
      if (found) return prev.map((i) => (i.id === id ? { ...i, qty: i.qty + qty } : i));
      return [...prev, { id, qty }];
    });
    if (!silent && item) {
      toast.success("Added to cart", { description: item.name });
    }
  };

  const setQty = (id, qty) => {
    if (qty <= 0) {
      setItems((prev) => prev.filter((i) => i.id !== id));
      return;
    }
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, qty } : i)));
  };

  const remove = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const clear = () => {
    setItems([]);
    setCoupon(null);
  };

  const applyCoupon = (code) => {
    if (code.trim().toUpperCase() === DEMO_COUPON) {
      setCoupon({ code: DEMO_COUPON, pct: 10 });
      return true;
    }
    return false;
  };
  const removeCoupon = () => setCoupon(null);

  const { subtotal, count } = useMemo(() => {
    let sub = 0;
    let c = 0;
    for (const i of items) {
      const item = byId.get(i.id);
      if (item) {
        sub += item.price * i.qty;
        c += i.qty;
      }
    }
    return { subtotal: sub, count: c };
  }, [items, byId]);

  const discount = coupon ? Math.round((subtotal * coupon.pct) / 100) : 0;
  const delivery = subtotal === 0 || subtotal - discount >= FREE_DELIVERY_THRESHOLD ? 0 : FLAT_DELIVERY;
  const total = subtotal - discount + delivery;

  return (
    <CartContext.Provider
      value={{
        items, add, setQty, remove, clear,
        subtotal, count, coupon, applyCoupon, removeCoupon,
        discount, delivery, total,
        resolve, catalogue, catalogueReady,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
